import { Button, Flex, Form, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { MixpanelClient } from '~/plugins/mixpanel/client'
import { useBlogPostContext } from '../../../../context'
import { ConnectionForm } from './components/ConnectionForm'
import { ConnectionSelector } from './components/ConnectionSelector'

type Props = {
  blogPostId: string
  onPublish: (platform: string) => void
  onBack: () => void
}

export type WordpressFormValues = {
  wordpressUrl: string
  username: string
  applicationPassword: string
  publishStatus: 'publish' | 'draft'
}

const ADD_NEW_CONNECTION = 'ADD_NEW'

export const WordpressFlow: React.FC<Props> = ({
  blogPostId,
  onPublish,
  onBack,
}) => {
  const mixpanel = MixpanelClient.use()

  const [form] = Form.useForm<WordpressFormValues>()
  const ctx = useBlogPostContext()

  const [providerAccountIdSelected, setProviderAccountIdSelected] = useState<
    string | null
  >(null)
  const [isLoadingPublish, setIsLoadingPublish] = useState(false)
  const [isLoadingConnections, setIsLoadingConnections] = useState(false)
  const [isConnectionError, setIsConnectionError] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const providerAccounts = useMemo(
    () => ctx.providerAccounts || [],
    [ctx.providerAccounts],
  )
  const isAddingNew = providerAccountIdSelected === ADD_NEW_CONNECTION

  const selectedProviderAccount = providerAccounts.find(
    providerAccount => providerAccount.id === providerAccountIdSelected,
  )

  useEffect(() => {
    if (providerAccounts.length > 0 && !providerAccountIdSelected) {
      setProviderAccountIdSelected(providerAccounts[0].id)
    }
  }, [providerAccounts, providerAccountIdSelected])

  useEffect(() => {
    form.setFieldsValue({ publishStatus: 'publish' })
  }, [form])

  const handleSaveNewConnection = async (values: WordpressFormValues) => {
    setIsLoadingConnections(true)
    setIsConnectionError(false)
    try {
      const providerAccount = await ctx.createOrUpdateConnection(values)

      const { success } = await ctx.checkConnection({
        providerAccountId: providerAccount.id,
      })

      if (!success) {
        mixpanel.track(`wordpress.connection.failed`)
        setIsConnectionError(true)
        return
      }
      mixpanel.track(`wordpress.connection.success`)

      form.resetFields()
      setProviderAccountIdSelected(null)
      setIsEditing(false)
    } catch (error) {
      console.error('Error while updating', error)
    } finally {
      setIsLoadingConnections(false)
    }
  }

  const handleDelete = async (providerAccountId: string) => {
    await ctx.deleteConnection({ providerAccountId })

    setProviderAccountIdSelected(providerAccounts?.[0]?.id)
  }

  const handleEdit = async (providerAccountId: string) => {
    const providerAccount = providerAccounts.find(
      providerAccount => providerAccount.id === providerAccountId,
    )

    if (providerAccount) {
      const connection = providerAccount.connection as any
      form.setFieldsValue({
        wordpressUrl: connection.wordpressUrl,
        username: connection.username,
        applicationPassword: connection.applicationPassword,
        publishStatus: connection.publishStatus,
      })
      setIsEditing(true)
      setProviderAccountIdSelected(providerAccountId)
    }
  }

  const handlePublish = async () => {
    setIsLoadingPublish(true)
    try {
      const providerAccount = providerAccounts.find(
        providerAccount => providerAccount.id === providerAccountIdSelected,
      )

      if (!providerAccount) {
        return message.error(
          'Could not find provider account. Refresh page and try again.',
        )
      }

      await ctx.publish({ blogPostId, providerAccount })
      onPublish('wordpress')
      message.success('Blog post published')
    } catch (error) {
      message.error('Could not publish post')
    } finally {
      setIsLoadingPublish(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setIsEditing(false)
    setProviderAccountIdSelected(null)
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <ConnectionSelector
        value={providerAccountIdSelected}
        onChange={setProviderAccountIdSelected}
        onEdit={handleEdit}
        onDelete={handleDelete}
        providerAccounts={providerAccounts}
        addNewValue={ADD_NEW_CONNECTION}
      />

      {(isAddingNew || isEditing) && (
        <ConnectionForm
          form={form}
          onCancel={handleCancel}
          onFinish={handleSaveNewConnection}
          isLoading={isLoadingConnections}
          isError={isConnectionError}
        />
      )}

      {!isAddingNew && !isEditing && providerAccountIdSelected && (
        <Flex justify="end" gap={8} className="mt-8">
          <Button onClick={onBack}>Back</Button>
          <Button
            type="primary"
            loading={isLoadingPublish}
            onClick={handlePublish}
            disabled={selectedProviderAccount?.status === 'CORRUPTED'}
          >
            Publish to WordPress
          </Button>
        </Flex>
      )}
    </div>
  )
}
